class TruncateText extends HTMLElement {
  connectedCallback() {
    const content = this.querySelector(".resale-report__truncate-content");
    const moreButton = this.querySelector(".resale-report__truncate-toggle--more");
    const lessButton = this.querySelector(".resale-report__truncate-toggle--less");
    if (!content || !moreButton || !lessButton) return;

    const lineCount = parseInt(this.getAttribute("lines"), 10) || 4;
    const HEIGHT_TOLERANCE_PX = 1;
    const FALLBACK_LINE_HEIGHT_RATIO = 1.2;

    const applyClamp = () => {
      content.style.setProperty("display", "-webkit-box");
      content.style.setProperty("-webkit-box-orient", "vertical");
      content.style.setProperty("-webkit-line-clamp", String(lineCount));
      content.style.setProperty("overflow", "hidden");
    };
    const removeClamp = () => {
      content.style.setProperty("display", "block");
      content.style.setProperty("-webkit-line-clamp", "unset");
      content.style.setProperty("overflow", "visible");
    };

    const needsClamping = () => {
      const style = getComputedStyle(content);
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * FALLBACK_LINE_HEIGHT_RATIO;
      const width = content.getBoundingClientRect().width;

      const clone = content.cloneNode(true);
      clone.removeAttribute("style");
      clone.style.cssText = [
        "position: fixed",
        "left: -99999px",
        "top: 0",
        `width: ${width}px`,
        "box-sizing: border-box",
        "display: block",
        "-webkit-line-clamp: none",
        "height: auto",
        "overflow: visible",
        "visibility: hidden",
        "pointer-events: none",
      ].join(";");
      content.parentNode.appendChild(clone);
      const fullHeight = clone.getBoundingClientRect().height;
      clone.remove();

      return fullHeight > lineHeight * lineCount + HEIGHT_TOLERANCE_PX;
    };

    const collapse = () => {
      applyClamp();
      moreButton.classList.remove("hidden");
      moreButton.setAttribute("aria-hidden", "false");
      moreButton.setAttribute("aria-expanded", "false");
      lessButton.classList.add("hidden");
      lessButton.setAttribute("aria-hidden", "true");
    };
    const expand = () => {
      removeClamp();
      moreButton.classList.add("hidden");
      moreButton.setAttribute("aria-hidden", "true");
      lessButton.classList.remove("hidden");
      lessButton.setAttribute("aria-hidden", "false");
      lessButton.setAttribute("aria-expanded", "true");
    };
    const hideToggle = () => {
      removeClamp();
      moreButton.classList.add("hidden");
      moreButton.setAttribute("aria-hidden", "true");
      lessButton.classList.add("hidden");
      lessButton.setAttribute("aria-hidden", "true");
    };

    moreButton.addEventListener("click", expand);
    lessButton.addEventListener("click", collapse);

    const refresh = () => {
      if (needsClamping()) collapse();
      else hideToggle();
    };

    requestAnimationFrame(refresh);
    window.addEventListener("load", refresh);

    let resizeRaf;
    let lastWidth = this.clientWidth;
    window.addEventListener("resize", () => {
      const width = this.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(refresh);
    });
  }
}

customElements.define("truncate-text", TruncateText);

class StickerCounter extends HTMLElement {
  connectedCallback() {
    const target = parseInt(this.dataset.target, 10) || 0;
    const valueEl = this.querySelector(".resale-report__image-with-text__sticker-value");
    if (!valueEl) return;

    const format = (n) => `${n > 0 ? "+" : ""}${n}%`;
    const DURATION_MS = 1500;

    const animate = () => {
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / DURATION_MS, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        valueEl.textContent = format(Math.round(target * eased));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 },
    );
    observer.observe(this);
  }
}

customElements.define("sticker-counter", StickerCounter);

function initResaleReportSliderDots() {
  document.querySelectorAll("slider-component .slideshow__control-wrapper").forEach((wrapper) => {
    if (wrapper.dataset.rrDotsBound) return;
    wrapper.dataset.rrDotsBound = "true";

    const dots = Array.from(wrapper.querySelectorAll(".slider-counter__link--dots"));
    if (!dots.length) return;

    const listId = dots[0].getAttribute("aria-controls");
    const list = listId && document.getElementById(listId);
    if (!list) return;

    const items = Array.from(list.children).filter((child) => child.id && child.id.indexOf("Slide-") === 0);

    dots.forEach((dot, index) => {
      const target = items[index];
      if (!target) return;
      dot.addEventListener("click", () => {
        list.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
      });
    });

    const setActiveDot = () => {
      // Only the first dots.length items have their own dot (each dot slides by one column,
      // not a whole page), so the "closest" search must stay within that range — otherwise a
      // scroll position past the last dot's target (clamped at the end of the list) would
      // match a later item with no corresponding dot at all.
      let closestIndex = 0;
      let closestDistance = Infinity;
      items.slice(0, dots.length).forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - list.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      dots.forEach((dot, index) => {
        dot.classList.toggle("slider-counter__link--active", index === closestIndex);
      });
    };

    // rAF-throttled: scroll fires far more often than the screen repaints, so this runs
    // setActiveDot at most once per frame instead of once per scroll event.
    let scrollScheduled = false;
    list.addEventListener("scroll", () => {
      if (scrollScheduled) return;
      scrollScheduled = true;
      requestAnimationFrame(() => {
        setActiveDot();
        scrollScheduled = false;
      });
    });
    setActiveDot();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initResaleReportSliderDots);
} else {
  initResaleReportSliderDots();
}
